import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { TimeHlp } from 'src/lib/fliko/helpers/time.helper';
import { Collection } from 'src/lib/fliko/types/database.type';

export class GroupValidators {
  public static endDateGreater(
    startKey: string,
    endKey: string,
    enabled: (control: AbstractControl) => boolean = () => true
  ): ValidatorFn {
    const err = 'beforeAfter';
    return (ctrl: AbstractControl) => {
      const start = ctrl.get(startKey);
      const end = ctrl.get(endKey);

      if (!start || !end) return null;

      if (!enabled(ctrl)) {
        this.removeError(start, err);
        this.removeError(end, err);
        return null;
      }

      if (start.value && end.value && TimeHlp.timestamp(start.value) > TimeHlp.timestamp(end.value)) {
        this.addError(start, err);
        this.addError(end, err);
        return { [err]: true };
      }
      this.removeError(start, err);
      this.removeError(end, err);
      return null;
    };
  }

  public static endTimeGreater(
    startKey: string,
    endKey: string,
    enabled: (control: AbstractControl) => boolean = () => true
  ): ValidatorFn {
    const err = 'beforeAfter';
    return (ctrl: AbstractControl) => {
      const start = ctrl.get(startKey);
      const end = ctrl.get(endKey);

      if (!start || !end) return null;

      if (!enabled(ctrl)) {
        this.removeError(start, err);
        this.removeError(end, err);
        return null;
      }

      const startMinutes = this.timeToMinutes(start.value);
      const endMinutes = this.timeToMinutes(end.value);
      if (startMinutes != null && endMinutes != null && startMinutes > endMinutes) {
        this.addError(start, err);
        this.addError(end, err);
        return { [err]: true };
      }
      this.removeError(start, err);
      this.removeError(end, err);
      return null;
    };
  }

  /**
   * Add error to control if not already exists
   * @param {AbstractControl} control
   * @param {string}          error   error object code
   */
  private static addError(control: AbstractControl, error: string): void {
    if (!control.hasError(error)) {
      const newError: Collection<boolean> = {};
      newError[error] = true;
      const errors = Object.assign({}, control.errors, newError);
      control.setErrors(errors);
    }
  }

  /**
   * Remove error from control if exists
   * @param {AbstractControl} control
   * @param {string}          error   error object code
   */
  private static removeError(control: AbstractControl, error: string): void {
    if (control.hasError(error)) {
      const errors = Object.assign({}, control.errors);
      delete errors[error];
      control.setErrors(ObjHlp.isEmpty(errors) ? null : errors);
    }
  }

  private static timeToMinutes(value: unknown): number | null {
    if (typeof value !== 'string') return null;

    const [hours, minutes] = value.split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return hours * 60 + minutes;
  }

  public static uniqueFromStringArray(values: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const uniqueValues = values.map((value) => value.trim().toLowerCase());
      const newValue = (control.value || '').trim().toLowerCase();
      return uniqueValues.includes(newValue) ? { uniqueValue: true } : null;
    };
  }

  public static dateOutOfRange(fromControlName: string, toControlName: string, minDate: Date, maxDate: Date): ValidatorFn {
    return (control: AbstractControl) => {
      const start = control.get(fromControlName);
      const end = control.get(toControlName);
      if (maxDate && start && end && start.value && end.value) {
        if (new Date(start.value).getTime() < new Date(minDate).getTime()) {
          start.setErrors({ outOfAgrRange: true });
        } else if (new Date(end.value).getTime() > new Date(maxDate).getTime()) {
          end.setErrors({ outOfAgrRange: true });
        } else {
          control.setErrors(null);
        }
      }
      return null;
    };
  }

  /**
   * Checks if values of given fields exists and are unique among them
   * @param keys names of checked fields
   * @return error object if invalid
   */
  public static uniqueFromFormGroup(keys: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const form = control as FormControl;

      for (let i = 0; i < keys.length; i++) {
        for (let j = i + 1; j < keys.length; j++) {
          const val1 = form.get(keys[i])?.value;
          const val2 = form.get(keys[j])?.value;
          if (val1 != null && val2 != null && val1 == val2) {
            return { uniqueValue: true };
          }
        }
      }

      return null;
    };
  }

  /**
   * Checks if any of given fields has value
   * @param keys   names of checked fields
   * @param labels of checked fields
   * @return error object if invalid
   */
  public static requiredAnyOf(keys: string[], labels: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const form = control as FormControl;

      for (const key of keys) {
        const fControl = form.get(key);
        if (fControl && fControl.value && fControl.valid) {
          return null;
        }
      }
      return { requiredAny: { fields: labels } };
    };
  }
}
